<template>
  <section class="office-directory-public-compact">
    <!-- LOADING -->
    <div
      v-if="loading"
      class="d-flex justify-center py-10"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="40"
      />
    </div>

    <!-- OFFICES -->
    <template v-else>
      <v-container
        v-for="office in visibleOffices"
        :key="office.apdid"
        class="office-section py-8"
        :style="{
          ...sysTypo.sectionTypography.body,
          color: themeStore.sectionTheme.footerColor
        }"
      >
        <div class="office-info">

          <!-- COMPLETE ADDRESS -->
          <div
            v-if="localizedAddress(office)"
            class="contact-row"
          >
            <div class="contact-icon">
              <v-icon
                :style="{
                  color: themeStore.sectionTheme.footerBtnColor
                }"
              >
                mdi-map-marker-outline
              </v-icon>
            </div>

            <div class="contact-content">
              <div
                class="contact-value"
                :style="{
                  ...sysTypo.sectionTypography.body,
                  color: themeStore.sectionTheme.footerColor
                }"
              >
                {{ localizedAddress(office) }}
              </div>
            </div>
          </div>

          <!-- TELEPHONE -->
          <div
            v-if="office.telnum"
            class="contact-row"
          >
            <div class="contact-icon">
              <v-icon
                :style="{
                  color: themeStore.sectionTheme.footerBtnColor
                }"
              >
                mdi-phone-outline
              </v-icon>
            </div>

            <div class="contact-content">
              <a
                :href="`tel:${office.telnum}`"
                class="contact-link"
                :style="{
                  ...sysTypo.sectionTypography.body,
                  color: themeStore.sectionTheme.footerColor
                }"
              >
                {{ office.telnum }}
              </a>
            </div>
          </div>

          <!-- EMAIL -->
          <div
            v-if="office.apdemail"
            class="contact-row"
          >
            <div class="contact-icon">
              <v-icon
                :style="{
                  color: themeStore.sectionTheme.footerBtnColor
                }"
              >
                mdi-email-outline
              </v-icon>
            </div>

            <div class="contact-content">
              <a
                :href="`mailto:${office.apdemail}`"
                class="contact-link"
                :style="{
                  ...sysTypo.sectionTypography.body,
                  color: themeStore.sectionTheme.footerColor
                }"
              >
                {{ office.apdemail }}
              </a>
            </div>
          </div>

          <!-- DETAILS -->
          <div
            v-if="localizedDetails(office)"
            class="office-details"
          >
            <!-- EMPTY ICON COLUMN TO KEEP LEFT ALIGNMENT -->
            <div class="contact-icon-placeholder"></div>

            <div
              class="contact-content"
              v-html="localizedDetails(office)"
              :style="{
                ...sysTypo.sectionTypography.body,
                color: themeStore.sectionTheme.footerColor
              }"
            />
          </div>

          <!-- GOOGLE MAPS BUTTON -->
          <div
            v-if="hasCoordinates(office)"
            class="office-action-row"
          >
            <div class="contact-icon-placeholder"></div>

            <div class="contact-content">
              <v-btn
              elevation="1"
                variant="text"
                density="comfortable"
                prepend-icon="mdi-google-maps"
                :href="getGoogleMapsLink(office)"
                target="_blank"
                rel="noopener noreferrer"
                class="maps-button"
                style="margin-left: -10px;"
                :style="{
                  ...sysTypo.sectionTypography.body,
                  color: themeStore.sectionTheme.footerBtnColor
                }"
              >
                {{
                  isFrench
                    ? 'Ouvrir dans Google Maps'
                    : 'Open in Google Maps'
                }}
              </v-btn>
            </div>
          </div>

        </div>
      </v-container>
    </template>
  </section>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref
} from 'vue'

import publicApi from '@/auth/publicApi'

import {
  useThemeStore
} from '@/store/auth'

import {
  useSysTypo
} from '@/store/useSysTypo'

const sysTypo =
  useSysTypo()

const themeStore =
  useThemeStore()

const props =
  defineProps({
    locale: {
      type: String,
      default: 'en'
    }
  })

const offices =
  ref([])

const loading =
  ref(false)

const isFrench =
  computed(() =>
    props.locale
      ?.toLowerCase()
      .startsWith('fr')
  )

const visibleOffices =
  computed(() => {
    return offices.value
      .filter(
        office =>
          office.isActive
      )
      .sort(
        (a, b) =>
          Number(
            a.displayOrder || 0
          ) -
          Number(
            b.displayOrder || 0
          )
      )
  })

const localizedOfficeName =
  office => {
    if (isFrench.value) {
      return (
        office.officeName_fr ||
        office.officeName ||
        'Bureau'
      )
    }

    return (
      office.officeName ||
      office.officeName_fr ||
      'Office'
    )
  }

const localizedAddress =
  office => {
    if (isFrench.value) {
      return (
        office.formattedAddress_fr ||
        office.officeAddress_fr ||
        office.formattedAddress ||
        office.officeAddress ||
        ''
      )
    }

    return (
      office.formattedAddress ||
      office.officeAddress ||
      office.formattedAddress_fr ||
      office.officeAddress_fr ||
      ''
    )
  }

const localizedDetails =
  office => {
    if (isFrench.value) {
      return (
        office.details_fr ||
        office.details ||
        ''
      )
    }

    return (
      office.details ||
      office.details_fr ||
      ''
    )
  }

const localizedAboutText =
  office => {
    if (isFrench.value) {
      return (
        office.apdtext_fr ||
        office.apdtext ||
        ''
      )
    }

    return (
      office.apdtext ||
      office.apdtext_fr ||
      ''
    )
  }

const hasCoordinates =
  office => {
    return (
      office &&
      office.latitude !== null &&
      office.latitude !== undefined &&
      office.longitude !== null &&
      office.longitude !== undefined
    )
  }

const getGoogleMapsLink =
  office => {
    if (!hasCoordinates(office)) {
      return '#'
    }

    const companyName =
      localizedOfficeName(
        office
      )

    const address =
      localizedAddress(
        office
      )

    const query =
      address
        ? `${companyName}, ${address}`
        : `${companyName}, ${office.latitude},${office.longitude}`

    return (
      'https://www.google.com/maps/search/?api=1&query=' +
      encodeURIComponent(
        query
      )
    )
  }

const loadOffices =
  async () => {
    loading.value = true

    try {
      const response =
        await publicApi.get(
          '/AboutPageDirectory'
        )

      offices.value =
        Array.isArray(
          response.data
        )
          ? response.data
          : []
    }
    catch (error) {
      console.error(
        '[AboutPageDirectoryPublicCompact] Failed loading offices:',
        error
      )

      offices.value = []
    }
    finally {
      loading.value = false
    }
  }

onMounted(() => {
  loadOffices()
})
</script>

<style scoped>
.office-directory-public-compact {
  width: 100%;
  text-align: left;
}

.office-section {
  width: 100%;
  max-width: 760px;
  margin-left: 0 !important;
  margin-right: auto !important;
  text-align: left;
}

.office-info {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

/* ============================================================
   CONTACT ROW
============================================================ */

.contact-row,
.office-details,
.office-action-row {
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
}

.contact-row {
  margin-bottom: 12px;
}

/* ============================================================
   ICON COLUMN
============================================================ */

.contact-icon,
.contact-icon-placeholder {
  flex: 0 0 32px;
  width: 32px;
  min-width: 32px;
}

.contact-icon {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding-top: 2px;
}

.contact-icon-placeholder {
  height: 1px;
}

/* ============================================================
   CONTENT COLUMN
============================================================ */

.contact-content {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.contact-value {
  width: 100%;
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
}

.contact-link {
  display: inline-block;
  color: inherit;
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}

/* ============================================================
   DETAILS
============================================================ */

.office-details {
  margin-top: 8px;
  line-height: 1.5;
}

.office-details :deep(p) {
  margin: 0 0 6px;
  text-align: left;
}

.office-details :deep(ul),
.office-details :deep(ol) {
  padding-left: 20px;
  margin: 6px 0;
  text-align: left;
}

/* ============================================================
   MAP BUTTON
============================================================ */

.office-action-row {
  margin-top: 10px;
}

.maps-button {
  justify-content: flex-start;
  padding-left: 0 !important;
  text-transform: none;
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 600px) {
  .office-section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .contact-icon,
  .contact-icon-placeholder {
    flex-basis: 28px;
    width: 28px;
    min-width: 28px;
  }
}
</style>